// src/components/maintenance/AdminMaintenanceToggle.jsx
//
// Floating control shown ONLY when an admin is unlocked. Maintenance v3 is
// per-surface, so this is a list rather than a single switch: one row per
// surface, each flipped independently via PUT /api/maintenance.
//
// Rows come from GET /api/maintenance (admin-only), which is the only endpoint
// that still returns the whole `surfaces` block — it was removed from the
// public read so a shared serializer cannot leak `updated_by` publicly.
//
// Props:
//   initialMaintenance (bool) - resolved web flag the gate already read
//   onChanged(bool)           - called with the RESOLVED web flag after a write
//   onLogout()                - called after the admin logs out

import React, { useState, useCallback, useEffect } from "react";
import { API_ENDPOINTS, MAINTENANCE_SURFACES, CLIENT_SURFACE } from "../../config/apiConfig";
import { authHeaders, clearAdminToken, getAdminToken } from "./adminAuth";
import AdminNoticeboard from "./AdminNoticeboard";
import "./AdminMaintenanceToggle.css";

// What this browser sees is global OR its own surface — the §2 rule, applied
// locally so the panel can report our own state without a second round trip.
const resolveForThisClient = (surfaces) =>
  Boolean(surfaces?.global?.maintenance) || Boolean(surfaces?.[CLIENT_SURFACE]?.maintenance);

const shortDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

export default function AdminMaintenanceToggle({
  initialMaintenance = true,
  onChanged,
  onLogout,
}) {
  const [open, setOpen] = useState(false);
  const [surfaces, setSurfaces] = useState(null);
  const [expanded, setExpanded] = useState(null); // surface id whose detail is open
  const [drafts, setDrafts] = useState({});       // surface id -> message being edited
  const [busy, setBusy] = useState(null);         // surface id currently writing
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [noticesOpen, setNoticesOpen] = useState(false); // noticeboard authoring screen

  // Resolved flag for the collapsed pill. Falls back to what the gate read
  // until the admin list has loaded.
  const webFlag = surfaces ? resolveForThisClient(surfaces) : initialMaintenance;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_ENDPOINTS.MAINTENANCE_ADMIN, {
        method: "GET",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        cache: "no-store",
      });
      if (res.status === 401) {
        clearAdminToken();
        if (onLogout) onLogout();
        return;
      }
      const data = await res.json();
      if (res.ok && data.status === "success" && data.surfaces) {
        setSurfaces(data.surfaces);
      } else {
        setError(data.reason || "Could not read maintenance state.");
      }
    } catch {
      setError("Server unreachable.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  // Load once the panel is actually opened — no cost while collapsed.
  useEffect(() => { if (open && !surfaces) load(); }, [open, surfaces, load]);

  const write = useCallback(
    async (surfaceId, nextMaintenance, nextMessage) => {
      setBusy(surfaceId);
      setError("");
      try {
        const body = { surface: surfaceId, maintenance: nextMaintenance };
        // Only send `message` when we mean to change it — omitting it leaves
        // the stored copy alone, while explicit null clears it.
        if (nextMessage !== undefined) body.message = nextMessage || null;

        const res = await fetch(API_ENDPOINTS.MAINTENANCE_SET, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(body),
        });

        if (res.status === 401) {
          clearAdminToken();
          if (onLogout) onLogout();
          return;
        }

        const data = await res.json();
        if (!res.ok || data.status !== "success") {
          setError(data.reason || "Update failed.");
          return;
        }

        // The write returns the same object as the admin read, so take the
        // whole block rather than patching one row.
        const next = data.surfaces || {
          ...surfaces,
          [surfaceId]: { maintenance: nextMaintenance, message: body.message ?? null },
        };
        setSurfaces(next);
        if (onChanged) onChanged(resolveForThisClient(next));
      } catch {
        setError("Server unreachable.");
      } finally {
        setBusy(null);
      }
    },
    [surfaces, onChanged, onLogout]
  );

  const doLogout = useCallback(async () => {
    // Best-effort server logout; the token is stateless so discarding it locally
    // is the real logout.
    try {
      if (getAdminToken()) {
        await fetch(API_ENDPOINTS.ADMIN_LOGOUT, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
        });
      }
    } catch {
      /* ignore */
    }
    clearAdminToken();
    if (onLogout) onLogout();
  }, [onLogout]);

  if (!open) {
    return (
      <div className="admin-toggle">
        <button className="admin-toggle-pill" onClick={() => setOpen(true)} title="Admin controls">
          <span className={`admin-dot ${webFlag ? "is-maint" : "is-live"}`} />
          Admin
        </button>
      </div>
    );
  }

  return (
    <div className="admin-toggle open">
      <div className="admin-toggle-panel">
        <div className="admin-toggle-row">
          <span className="admin-toggle-label">Maintenance</span>
          <button className="admin-toggle-x" onClick={() => setOpen(false)} aria-label="collapse">×</button>
        </div>

        {loading && !surfaces && <div className="admin-surface-loading">Reading state…</div>}

        {surfaces && (
          <ul className="admin-surface-list">
            {MAINTENANCE_SURFACES.map(({ id, label, hint }) => {
              const row = surfaces[id];
              // A surface the backend doesn't return yet — show it, but don't
              // pretend to know its state.
              const known = Boolean(row);
              const on = Boolean(row?.maintenance);
              const isSelf = id === CLIENT_SURFACE;
              const isOpen = expanded === id;
              const writing = busy === id;

              return (
                <li
                  key={id}
                  className={`admin-surface${on ? " is-on" : ""}${id === "global" ? " is-global" : ""}${isOpen ? " is-expanded" : ""}`}
                >
                  <div className="admin-surface-head">
                    <button
                      className="admin-surface-name"
                      onClick={() => setExpanded(isOpen ? null : id)}
                      aria-expanded={isOpen}
                      title={hint}
                    >
                      <span className={`admin-dot ${on ? "is-maint" : "is-live"}`} />
                      <span className="admin-surface-text">
                        {label}
                        {isSelf && <span className="admin-surface-self">this site</span>}
                      </span>
                      <code className="admin-surface-id">{id}</code>
                    </button>

                    <button
                      className={`admin-surface-switch${on ? " on" : ""}`}
                      disabled={!known || writing}
                      onClick={() => write(id, !on)}
                      title={known ? (on ? `Take ${label} live` : `Put ${label} in maintenance`) : "Not reported by the backend"}
                      aria-pressed={on}
                    >
                      <span className="admin-surface-knob" />
                    </button>
                  </div>

                  {isOpen && known && (
                    <div className="admin-surface-detail">
                      <p className="admin-surface-hint">{hint}</p>

                      <label className="admin-surface-msg-label" htmlFor={`msg-${id}`}>
                        Message shown while gated
                      </label>
                      <textarea
                        id={`msg-${id}`}
                        className="admin-surface-msg"
                        rows={2}
                        placeholder="Leave blank to use the client's own wording"
                        value={drafts[id] ?? row.message ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
                      />
                      <div className="admin-surface-detail-actions">
                        <button
                          className="admin-surface-save"
                          disabled={writing || (drafts[id] ?? row.message ?? "") === (row.message ?? "")}
                          onClick={() => write(id, on, drafts[id] ?? "")}
                        >
                          {writing ? "Saving…" : "Save message"}
                        </button>
                      </div>

                      {(row.updated_by || row.updated_at) && (
                        <p className="admin-surface-meta">
                          Last changed
                          {row.updated_by ? ` by ${row.updated_by}` : ""}
                          {shortDate(row.updated_at) ? ` · ${shortDate(row.updated_at)}` : ""}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {surfaces?.global?.maintenance && (
          <div className="admin-surface-warn">
            <strong>Everything</strong> is gated — this overrides all per-surface switches.
          </div>
        )}

        {error && <div className="admin-toggle-error">{error}</div>}

        {/* Advisory notices are authored here; the switches above stay the barrier. */}
        <button className="admin-toggle-logout" onClick={() => setNoticesOpen(true)}>Noticeboard…</button>
        <button className="admin-toggle-logout" onClick={doLogout}>Log out admin</button>
        {noticesOpen && <AdminNoticeboard onClose={() => setNoticesOpen(false)} onLogout={onLogout} />}
      </div>
    </div>
  );
}
